import { Box } from '@mui/material';
import Divider from '@mui/material/Divider';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DropdownV2 from 'components/DropdownComponentV2';
import ModalComponent from '../../../../../components/ModalComponent';

import {
  IRecognitionSimpleProps,
  IRecognitionTypes
} from '../../../../../services/Recognition/types';

import { useTranslation } from 'react-i18next';
import { getRecognitionTypesOptions } from '../../utils';
import CustomerRecognitionEditForm from '../forms/CustomerRecognitionEditForm';
import GeneralRecognitionEditForm from '../forms/GeneralRecognitionEditForm';
import ResponsibleRecognitionEditForm from '../forms/ResponsibleRecognitionEditForm';
import StudentRecognitionEditForm from '../forms/StudentRecognitionEditForm';

interface IRecognitionEditModalProps {
  isOpen: boolean;
  selectedRecognition?: IRecognitionSimpleProps;
  handleSaveForm: () => void;
  handleClose: () => void;
}

export const RecognitionsEditModal = ({
  isOpen,
  handleSaveForm,
  handleClose,
  selectedRecognition,
}: IRecognitionEditModalProps) => {
  const { t } = useTranslation();
  const [selectedRecognitionType, setSelectedRecognitionType] = useState<IRecognitionTypes | undefined>(undefined);

  useEffect(() => {
    if (isOpen) {
      setSelectedRecognitionType(
        selectedRecognition?.discriminator as IRecognitionTypes | undefined
      );
    }
  }, [isOpen, selectedRecognition]);

  const faceOptions = useMemo(() => getRecognitionTypesOptions(), []);

  const formattedData = useMemo(() => {
    if (!selectedRecognition) return undefined;

    const data = { ...selectedRecognition };

    if (data.discriminator === IRecognitionTypes.STUDENT) {
      data.registration = data.registration || data.userId;
    } else {
      data.cpf = data.cpf || data.userId;
    }

    return data;
  }, [selectedRecognition]);

  const onClose = useCallback(() => {
    setSelectedRecognitionType(undefined);
    handleClose();
  }, [handleClose]);

  const onSaveForm = useCallback(() => {
    handleSaveForm();
  }, [handleSaveForm]);

  const recognitionFormContent = useMemo(() => {
    if (!selectedRecognitionType) return null;

    const id = selectedRecognition?.id;

    switch (selectedRecognitionType) {
      case IRecognitionTypes.GENERAL:
        return <GeneralRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      case IRecognitionTypes.CUSTOMER:
        return <CustomerRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      case IRecognitionTypes.STUDENT:
        return <StudentRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      case IRecognitionTypes.RESPONSIBLE:
        return <ResponsibleRecognitionEditForm handleSaveForm={onSaveForm} selectedRecognitionId={id} selectedRecognitionData={formattedData} />;
      default:
        return <>{t('recognitionPage.modalEditFace.typesOfResponsible.faceTypeNotFound')}</>;
    }
  }, [selectedRecognitionType, selectedRecognition?.id, formattedData, onSaveForm, t]);

  return (
    <ModalComponent
      title={
        selectedRecognition
          ? t('recognitionPage.modalEditFace.editFace')
          : t('recognitionPage.modalEditFace.registerFace')
      }
      isOpen={isOpen}
      onClose={onClose}
    >
      <DropdownV2
        disabled={!!(selectedRecognition && selectedRecognition.discriminator)}
        label={t('recognitionPage.modalEditFace.typesOfResponsible.faceType')}
        value={selectedRecognitionType ?? ''}
        optionLabel="label"
        optionValue="value"
        options={faceOptions}
        onChange={setSelectedRecognitionType}
      />

      {selectedRecognitionType && (
        <>
          <Divider sx={{ mt: 2 }} variant="middle" color={'#b7b7b7'} />
          <Box key={selectedRecognitionType} sx={{ mt: 4 }}>
            {recognitionFormContent}
          </Box>
        </>
      )}
    </ModalComponent>
  );
};

export default RecognitionsEditModal;
